import React, { useState, useLayoutEffect, useCallback, useRef } from 'react';

const Tour = ({ isOpen, onClose, steps, helpers }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [highlightStyle, setHighlightStyle] = useState({});
  const [tooltipStyle, setTooltipStyle] = useState({});
  const nextButtonRef = useRef(null);

  const step = steps[currentStepIndex];

  const handleClose = useCallback(() => {
    setCurrentStepIndex(0);
    onClose();
  }, [onClose]);

  const goToStep = useCallback((index) => {
    const nextStep = steps[index];
    if (nextStep && nextStep.onLoad) {
      nextStep.onLoad(helpers);
    }
    setCurrentStepIndex(index);
  }, [steps, helpers]);

  const handleNext = useCallback(() => {
    const currentStep = steps[currentStepIndex];
    if (currentStep && currentStep.onNext) {
      currentStep.onNext(helpers);
    }

    if (currentStepIndex < steps.length - 1) {
      goToStep(currentStepIndex + 1);
    } else {
      handleClose();
    }
  }, [currentStepIndex, steps, goToStep, handleClose, helpers]);

  useLayoutEffect(() => {
    if (isOpen && step) {
      const executeStep = () => {
        const targetElement = step.target ? document.querySelector(step.target) : null;
        const isVisible = targetElement ? targetElement.offsetParent !== null : false;

        const focusNext = () => {
          if (nextButtonRef.current) {
            nextButtonRef.current.focus({ preventScroll: true });
          }
        };

        if (step.placement !== 'center' && !isVisible) {
          console.warn(`Tour target not found or not visible, skipping: ${step.target}`);
          handleNext();
          return;
        }

        if (step.placement !== 'center' && targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
          
        const updatePosition = () => {
          if (step.placement === 'center' || !targetElement) {
            setHighlightStyle({ display: 'none' });
            setTooltipStyle({
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10000,
            });
            return;
          }

          const rect = targetElement.getBoundingClientRect();
          setHighlightStyle({
            width: `${rect.width + 8}px`,
            height: `${rect.height + 8}px`,
            top: `${rect.top - 4}px`,
            left: `${rect.left - 4}px`,
          });

          const tooltipNode = document.getElementById('tour-tooltip');
          const tooltipHeight = tooltipNode ? tooltipNode.offsetHeight : 150;
          const tooltipWidth = tooltipNode ? tooltipNode.offsetWidth : 300;
          const margin = 20;
          const viewportMargin = 10;

          let top, left;

          switch (step.placement) {
            case 'right':
              top = rect.top;
              left = rect.right + margin;
              break;
            case 'left':
              top = rect.top;
              left = rect.left - tooltipWidth - margin;
              break;
            case 'top':
              top = rect.top - tooltipHeight - margin;
              left = rect.left;
              break;
            case 'bottom':
            default:
              top = rect.bottom + margin;
              left = rect.left;
              break;
          }

          const windowWidth = window.innerWidth;
          const windowHeight = window.innerHeight;

          if (top < viewportMargin) top = viewportMargin;
          if (top + tooltipHeight + viewportMargin > windowHeight) top = windowHeight - tooltipHeight - viewportMargin;
          if (left < viewportMargin) left = viewportMargin;
          if (left + tooltipWidth + viewportMargin > windowWidth) left = windowWidth - tooltipWidth - viewportMargin;

          setTooltipStyle({
            position: 'fixed',
            zIndex: 10000,
            top: `${top}px`,
            left: `${left}px`,
          });
        };
        
        setTimeout(() => {
          updatePosition();
          focusNext();
        }, 150);
      }

      if (step.delay) {
        setTimeout(executeStep, step.delay);
      } else {
        executeStep();
      }
    }
  }, [isOpen, step, helpers, handleNext]);

  const handlePrev = useCallback(() => {
    if (currentStepIndex > 0) {
      let prevIndex = currentStepIndex - 1;
      while (prevIndex >= 0) {
        const prevStep = steps[prevIndex];
        if (prevStep.placement === 'center') {
          goToStep(prevIndex);
          return;
        }
        
        const targetElement = prevStep.target ? document.querySelector(prevStep.target) : null;
        if (targetElement && targetElement.offsetParent !== null) {
          goToStep(prevIndex);
          return;
        }
        prevIndex--;
      }
    }
  }, [currentStepIndex, steps, goToStep]);

  if (!isOpen || !step) {
    return null;
  }

  return (
    <>
      <div className="tour-highlight" style={highlightStyle}></div>
      <div id="tour-tooltip" className={`tour-tooltip tour-tooltip-${step.placement || 'bottom'}`} style={tooltipStyle}>
        <div className="tour-content">{step.content}</div>
        <div className="tour-footer">
          <span>{currentStepIndex + 1} / {steps.length}</span>
          <div className="tour-footer-buttons">
            {currentStepIndex > 0 && <button title="Previous step" onClick={handlePrev}>Previous</button>}
            <button title="Next step" ref={nextButtonRef} onClick={handleNext}>{currentStepIndex === steps.length - 1 ? 'Finish' : 'Next'}</button>
            <button title="Close tour" onClick={handleClose} className="tour-close-button">Close</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Tour;